import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useMutation } from 'urql';

import {
  Card,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  Form,
  SubmitButton,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { limitFragment } from '@/fragments/Limits';
import { LimitTypeEnum } from '@/globalTypes';
import { useIsMounted } from '@/hooks';
import { Nullable } from '@/types';
import {
  RemoveLossLimitMutation,
  RemoveLossLimitMutationVariables,
} from './__generated__/PlayerRemoveLossLimitForm';

const query = graphql`
  query SanityPlayerRemoveLossLimitForm {
    sanityPlayerRemoveLossLimitsForm {
      title {
        ...SanityLocaleString
      }
      submit {
        ...SanityLocaleString
      }
    }
  }
`;

const removeLossLimitMutation = gql`
  mutation RemoveLossLimit($playerId: ID!, $limitPeriodType: LimitTypeEnum!) {
    removeLossLimit(playerId: $playerId, limitPeriodType: $limitPeriodType) {
      id
      lossLimit {
        dayLimit {
          ...PeriodLimit
        }
        weekLimit {
          ...PeriodLimit
        }
        monthLimit {
          ...PeriodLimit
        }
      }
    }
  }
  ${limitFragment}
`;

const PlayerRemoveLossLimitForm: FC<{
  playerId: string;
  limitPeriodType: LimitTypeEnum;
}> = ({ playerId, limitPeriodType }) => {
  const staticData =
    useStaticQuery<Queries.SanityPlayerRemoveLossLimitFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();

  const form = staticData.sanityPlayerRemoveLossLimitsForm;

  const [removePlayerLossLimitState, removePlayerLossLimit] = useMutation<
    RemoveLossLimitMutation,
    RemoveLossLimitMutationVariables
  >(removeLossLimitMutation);

  const { close } = useModalContext();

  const { t } = useTranslate();

  const onSubmit = (_values: {}) => {
    if (playerId) {
      removePlayerLossLimit({
        playerId,
        limitPeriodType,
      }).then((res) => {
        if (res.error?.message && isMounted) {
          setErrorMessage(res.error?.message);
          return;
        }
        if (close) {
          close();
        }
      });
    }
  };

  if (!form) {
    return null;
  }
  return (
    <Card
      size="lg"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="p-3">
        <Form
          defaultValues={{}}
          onSubmit={onSubmit}
          className="grid grid-cols-2 sm:grid-cols-3 gap-6"
        >
          <ErrorMessage message={errorMessage} />
          <SubmitButton
            value={t(form.submit)}
            disabled={removePlayerLossLimitState.fetching}
          />
        </Form>
      </div>
    </Card>
  );
};

export default PlayerRemoveLossLimitForm;
